import { Checkbox, Col, Flex, Form, Input, Row, Select, Typography } from 'antd'
import { Controller, FieldErrors } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { useTranslation } from 'react-i18next'
import { usePhone } from 'src/app/shared/hooks/usePhone'

interface BankInfoProps {
  control: any
  errors: FieldErrors<any>
  getValues: any
  setValue?: any
  trigger?: any
  isDisable?: boolean
}

function BankInfo(props: Readonly<BankInfoProps>) {
  const { control, errors, isDisable, setValue, trigger } = props
  const { t } = useTranslation()
  usePhone({
    ids: ['primaryPhoneNumber', 'mobileNumber'],
    setValue,
    trigger,
  })

  return (
    <Flex vertical>
      <Row gutter={24}>
        <Col span={24}>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item label={t('BankSetup:addNewBankUser.label.userName')} className='isRequired'>
                <Controller
                  name='username'
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      status={fieldState.error ? 'error' : ''}
                      placeholder={t('BankSetup:addNewBankUser.placeholder.enterUsername')}
                      onChange={(e) => {
                        field.onChange(e?.target?.value.replace(/\s/g, ''))
                        trigger('username')
                      }}
                      onBlur={(e) => {
                        field.onChange(e?.target?.value?.trim())
                      }}
                      maxLength={50}
                      disabled={isDisable}
                    />
                  )}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='username' />
                </Typography>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24} md={12}>
              <Form.Item label={t('BankSetup:addNewBankUser.label.firstName')} className='isRequired'>
                <Controller
                  name='firstName'
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      status={fieldState.error ? 'error' : ''}
                      placeholder={t('BankSetup:addNewBankUser.placeholder.enterFirstName')}
                      maxLength={64}
                      onChange={(e) => {
                        field.onChange(e)
                        trigger('firstName')
                      }}
                      onBlur={(e) => {
                        field.onChange(e?.target?.value?.trim())
                      }}
                    />
                  )}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='firstName' />
                </Typography>
              </Form.Item>
            </Col>
            <Col span={24} md={12}>
              <Form.Item label={t('BankSetup:addNewBankUser.label.lastName')} className='isRequired'>
                <Controller
                  name='lastName'
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      status={fieldState.error ? 'error' : ''}
                      placeholder={t('BankSetup:addNewBankUser.placeholder.enterLastName')}
                      maxLength={64}
                      onChange={(e) => {
                        field.onChange(e)
                        trigger('lastName')
                      }}
                      onBlur={(e) => {
                        field.onChange(e?.target?.value?.trim())
                      }}
                    />
                  )}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='lastName' />
                </Typography>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item label={t('BankSetup:addNewBankUser.label.emailAddress')} className='isRequired'>
                <Controller
                  name='email'
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      status={fieldState.error ? 'error' : ''}
                      placeholder={t('BankSetup:addNewBankUser.placeholder.enterEmailAddress')}
                      onChange={(e) => {
                        field.onChange(e?.target?.value)
                        trigger('email')
                      }}
                      onBlur={(e) => {
                        field.onChange(e?.target?.value?.trim())
                      }}
                      maxLength={70}
                    />
                  )}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='email' />
                </Typography>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={24} xl={12}>
              <Form.Item valuePropName='checked'>
                <Controller
                  name='isNotReceiveOtherMail'
                  control={control}
                  render={({ field }) => (
                    <Checkbox {...field} checked={field.value}>
                      {t('BankSetup:addNewBankUser.label.doNotSendEmailsToThisAddress')}
                    </Checkbox>
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24} xl={12}>
              <Form.Item label={t('BankSetup:addNewBankUser.label.primaryPhoneNumber')}>
                <Row gutter={10}>
                  <Col span={8} sm={5} md={3}>
                    <Controller
                      name='primaryPrefix'
                      control={control}
                      render={({ field }) => (
                        <Select
                          value={!field.value ? '1' : field.value}
                          onChange={field.onChange}
                          onBlur={async (event) => {
                            field.onBlur()
                          }}
                          options={[{ label: '+1', value: '1' }]}
                        />
                      )}
                    />
                  </Col>
                  <Col span={16} sm={19} md={21}>
                    <Controller
                      name='primaryPhoneNumber'
                      control={control}
                      render={({ field, fieldState }) => (
                        <Input
                          {...field}
                          id='primaryPhoneNumber'
                          status={fieldState.error ? 'error' : ''}
                          placeholder={t('BankSetup:addNewBankUser.placeholder.enterPrimaryPhoneNumber')}
                          maxLength={12}
                          onChange={(e) => {
                            let inputValue = e.target.value
                            inputValue = inputValue.trim().replace(/\D/g, '')
                            field.onChange(inputValue)
                          }}
                          onBlur={(e) => {
                            field.onChange(e?.target?.value?.trim())
                          }}
                        />
                      )}
                    />
                  </Col>
                </Row>
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='primaryPhoneNumber' />
                </Typography>
              </Form.Item>
            </Col>
            <Col span={24} xl={12}>
              <Form.Item label={t('BankSetup:addNewBankUser.label.mobileNumber')}>
                <Row gutter={10}>
                  <Col span={8} sm={5} md={3}>
                    <Controller
                      name='mobilePrefix'
                      control={control}
                      render={({ field }) => (
                        <Select
                          value={!field.value ? '1' : field.value}
                          onChange={field.onChange}
                          onBlur={async (event) => {
                            field.onBlur()
                          }}
                          options={[{ label: '+1', value: '1' }]}
                        />
                      )}
                    />
                  </Col>
                  <Col span={16} sm={19} md={21}>
                    <Controller
                      name='mobileNumber'
                      control={control}
                      render={({ field, fieldState }) => (
                        <Input
                          {...field}
                          id='mobileNumber'
                          status={fieldState.error ? 'error' : ''}
                          placeholder={t('BankSetup:addNewBankUser.placeholder.enterMobileNumber')}
                          maxLength={12}
                          onChange={(e) => {
                            let inputValue = e.target.value
                            inputValue = inputValue.trim().replace(/\D/g, '')
                            field.onChange(inputValue)
                          }}
                          onBlur={(e) => {
                            field.onChange(e?.target?.value?.trim())
                          }}
                        />
                      )}
                    />
                  </Col>
                </Row>
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='mobileNumber' />
                </Typography>
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
    </Flex>
  )
}

export default BankInfo
